import { useContext, useEffect, useRef, useState, type PointerEvent, type ReactElement } from 'react';
import { useChatFrame } from '../chat-frame';
import { arrivalHref, BANNER_QUIET_MS, bannerFor, type BannerWhere, type ChatArrival } from '../chats';
import { useChatsSnapshot } from '../chats-store';
import { ChatPicture } from '../pages/Chats';
import { navigate } from '../router';
import { TalkSheetOpenContext } from '../talk-sheet';
import { NOTIFY_OPEN_EVENT, notifyArrival } from '../desktop-notify';
import './ChatBanner.css';

const SHOWN_MS = 4000;
const LEAVE_MS = 320;
const SWIPE_PX = 30;

/** A line that landed while the tab was away still drops in on the way back, if it is this recent. */
const AWAY_KEEP_MS = 60_000;

/**
 * The banner (2026-10-20; mock chats-list-banner-2026-10-20): a line landed in another conversation, or an invitation
 * came. It drops from the top — the conversation's picture and name, "now", then who said what (the name in semibold,
 * two lines at most) — stays four seconds and goes; a swipe up sends it at once, a tap opens that conversation (whose
 * Back is the list). Since 2026-10-26 it stands over every page but the ones where nothing pops (chats.ts
 * bannerWhereOf) and the telling doors. A second line from the same conversation while it is up changes its words in
 * place and starts the four seconds again; another conversation's takes its place; nothing stacks. A conversation
 * whose banner was swiped away waits five minutes before the next (BANNER_QUIET_MS, the apps' too).
 */
/** Kept across pages: moving on does not let a conversation that was just quieted ring again. */
const quiet = new Map<string, number>();

export function ChatBanner({ where }: { where: BannerWhere }): ReactElement | null {
  const { skin } = useChatFrame();
  const { arrivals } = useChatsSnapshot();
  const doorsOpen = useContext(TalkSheetOpenContext);
  const [shown, setShown] = useState<{ a: ChatArrival; n: number } | null>(null);
  const [leaving, setLeaving] = useState<'' | 'shown' | 'swiped'>('');
  const [drag, setDrag] = useState(0);
  const seq = useRef(arrivals.seq);
  const whereRef = useRef(where);
  whereRef.current = doorsOpen ? { kind: 'off' } : where;
  const press = useRef<{ y: number; moved: boolean } | null>(null);
  // The newest line that landed while the tab was away, and when (2026-10-26 Riley)
  const away = useRef<{ a: ChatArrival; at: number } | null>(null);

  const drop = (a: ChatArrival): void => {
    // the same conversation: its words change in place; another one: a new banner (`n`, seen once)
    setShown((cur) => ({ a, n: cur && cur.a.key === a.key ? cur.n : (cur?.n ?? 0) + 1 }));
    setLeaving('');
    setDrag(0);
  };

  useEffect(() => {
    if (arrivals.seq === seq.current) return;
    seq.current = arrivals.seq;
    // The tab is away (2026-10-26 Riley, as WhatsApp Web): no banner nobody sees — the system's notification, if the
    // person said yes to it, for any page but a call or sign-in (they are not looking at the page), and the line is
    // kept for the way back.
    if (document.visibilityState === 'hidden') {
      if (whereRef.current.kind === 'off') return;
      const a = bannerFor(arrivals.list, { kind: 'page' }, quiet);
      if (!a) return;
      away.current = { a, at: Date.now() };
      void notifyArrival(a);
      return;
    }
    const a = bannerFor(arrivals.list, whereRef.current, quiet);
    if (!a) return;
    drop(a);
  }, [arrivals]);

  // Back in the tab: the line that landed while it was away drops in, when recent and the page would show it; opened
  // from its notification, it is where the person is going and needs none.
  useEffect(() => {
    const back = (): void => {
      if (document.visibilityState !== 'visible') return;
      const held = away.current;
      away.current = null;
      if (!held || Date.now() - held.at > AWAY_KEEP_MS) return;
      const a = bannerFor([held.a], whereRef.current, quiet);
      if (a) drop(a);
    };
    const opened = (): void => { away.current = null; };
    document.addEventListener('visibilitychange', back);
    window.addEventListener(NOTIFY_OPEN_EVENT, opened);
    return () => {
      document.removeEventListener('visibilitychange', back);
      window.removeEventListener(NOTIFY_OPEN_EVENT, opened);
    };
  }, []);

  // Four seconds from the latest line, then it goes up and away.
  useEffect(() => {
    if (!shown || leaving) return undefined;
    const t = window.setTimeout(() => setLeaving('shown'), SHOWN_MS);
    return () => window.clearTimeout(t);
  }, [shown, leaving]);
  useEffect(() => {
    if (!leaving || !shown) return undefined;
    if (BANNER_QUIET_MS[leaving]) quiet.set(shown.a.key, Date.now() + BANNER_QUIET_MS[leaving]);
    const t = window.setTimeout(() => { setShown(null); setLeaving(''); setDrag(0); }, LEAVE_MS);
    return () => window.clearTimeout(t);
  }, [leaving]);

  // Moving to another page, or the telling doors opening, takes it away: it was about a place the person has now
  // left, or gone to.
  const whereKey = doorsOpen ? 'doors' : where.kind === 'room' ? `room:${where.roomId}` : where.kind;
  useEffect(() => { setShown(null); setLeaving(''); }, [whereKey]);

  if (!shown) return null;
  const { a, n } = shown;

  const down = (e: PointerEvent<HTMLButtonElement>): void => { press.current = { y: e.clientY, moved: false }; };
  const move = (e: PointerEvent<HTMLButtonElement>): void => {
    const p = press.current;
    if (!p) return;
    const dy = e.clientY - p.y;
    if (dy < -4) p.moved = true;
    setDrag(dy < 0 ? dy : 0);
  };
  const up = (): void => {
    const p = press.current;
    if (!p) return;
    if (drag < -SWIPE_PX) setLeaving('swiped');
    else setDrag(0);
    window.setTimeout(() => { press.current = null; }, 0);
  };

  return (
    <div className="chat-banner-layer" data-chat-skin={skin} aria-live="polite">
      <button
        type="button"
        className={`chat-banner${leaving ? ' is-leaving' : ''}${drag ? ' is-dragged' : ''}`}
        style={drag ? { transform: `translateY(${drag}px)` } : undefined}
        data-log="chat_banner"
        data-log-state={a.state}
        data-log-match-id={a.roomId || undefined}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        onClick={() => {
          if (press.current?.moved) return;
          setShown(null);
          navigate(arrivalHref(a));
        }}
      >
        {/* Its own node per banner, so each one that drops in is seen (ui.seen observes a node once). */}
        <span key={n} className="chat-banner-body" data-log-seen="chat_banner" data-log-state={a.state} data-log-match-id={a.roomId || undefined}>
          <ChatPicture kind={a.state === 'invite' ? 'invite' : a.state === 'ember' ? 'ember' : 'room'} people={a.people} roomId={a.roomId} roomKind={a.roomKind} />
          <span className="chat-banner-text">
            <span className="chat-banner-head"><span dir="auto">{a.name}</span><small>now</small></span>
            <span className="chat-banner-line" dir="auto">{a.who ? <><b>{a.who}:</b> {a.text}</> : a.text}</span>
          </span>
        </span>
      </button>
    </div>
  );
}
